import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { REASON_SCHEMA } from './schemas';
import { ReasonField } from './reason-field';

const SCHEMA = z.object({ reason: REASON_SCHEMA });

interface ReasonPanelProps {
  title: string;
  description: string;
  submitLabel: string;
  destructive?: boolean;
  submitting: boolean;
  onSubmit: (args: { reason: string }) => void;
  onCancel: () => void;
  children?: ReactNode;
}

/**
 * Confirmation form for an email change that only needs a reason, recorded in the audit log.
 *
 * @param props.destructive - Styles the submit button as destructive.
 * @param props.children - Extra content shown above the reason field.
 */
export function ReasonPanel({
  title,
  description,
  submitLabel,
  destructive,
  submitting,
  onSubmit,
  onCancel,
  children,
}: ReasonPanelProps) {
  const form = useForm<z.infer<typeof SCHEMA>>({
    resolver: zodResolver(SCHEMA),
    defaultValues: { reason: '' },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ reason }) => onSubmit({ reason }))}
        className="space-y-3 rounded-lg border border-primary p-4">
        <h3 className="font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
        {children}
        <ReasonField control={form.control} disabled={submitting} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={destructive ? 'destructive' : 'default'}
            disabled={submitting}>
            {submitting ? 'Working...' : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
